import React, { useEffect, useState, useCallback } from 'react';
import { Modal, SparkModalPortal, Spinner } from '@spark';
import ModelDetailsQualityBenchmarksTable from '@/features/trainings/components/ModelDetailsQualityBenchmarksTable';
import { fetchModelDetails } from '@/features/trainings/api/modelDetailsApi';
import { modelDrawerDownloadBenchmark } from '@/features/trainings/components/modelDrawerTrainActions';

/**
 * "Replace Benchmark" modal opened from the Evaluation page's "To" dropdown
 * (the switch-horizontal icon shown on major versions with more than one
 * evaluation run). Reuses the Model Details drawer's Benchmarks table so the
 * user can pick which run's benchmark feeds the evaluation results.
 *
 * Selection is local until "Replace" is pressed; that locks the run
 * (session-only, via `selected_distillation_event_id`) and closes the modal.
 */
const ReplaceBenchmarkModal = ({
  open,
  clientId,
  usecaseName = null,
  /** Any distillation event id belonging to the target major version. */
  anchorEventId = null,
  /** Event id of the run currently active for this major (preselects its row). */
  activeBenchmarkEventId = null,
  /**
   * Event id of the run whose benchmark the Evaluation page is currently
   * showing. Picking that row disables "Replace" — replacing the displayed
   * benchmark with itself is a no-op.
   */
  currentEventId = null,
  /**
   * Display name of the current teacher model, e.g. "o3 (AzureAI)". Shown in
   * the metric cells' "vs …" lines instead of the generic "vs Teacher".
   */
  teacherName = null,
  onCancel,
  /** Called with (eventId, version) of the picked benchmark row. */
  onReplace,
}) => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [taskType, setTaskType] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(null);

  useEffect(() => {
    if (!open || !anchorEventId) return;
    let cancelled = false;
    setLoading(true);
    setEntries([]);
    setSelectedRowKey(null);
    fetchModelDetails({ client_id: clientId, distillation_event_id: anchorEventId })
      .then((details) => {
        if (cancelled) return;
        if (details?.success === false) {
          setEntries([]);
          setTaskType(null);
          return;
        }
        const rows = Array.isArray(details?.benchmarks_entries) ? details.benchmarks_entries : [];
        setEntries(rows);
        setTaskType(details?.distillation_event?.task_type ?? null);
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, clientId, anchorEventId]);

  useEffect(() => {
    if (!entries.length) return;
    if (
      activeBenchmarkEventId &&
      entries.some((row) => row.distillation_event_id === activeBenchmarkEventId)
    ) {
      setSelectedRowKey(activeBenchmarkEventId);
    }
  }, [entries, activeBenchmarkEventId]);

  const handleDownload = useCallback(
    (record) =>
      modelDrawerDownloadBenchmark({
        client_id: clientId,
        distillation_event_id: record?.distillation_event_id,
        version: record?.version,
        usecase_name: usecaseName,
      }),
    [clientId, usecaseName],
  );

  const replaceDisabled =
    !selectedRowKey || (currentEventId != null && selectedRowKey === currentEventId);

  const handleReplace = () => {
    if (replaceDisabled) return;
    const row = entries.find((e) => e.distillation_event_id === selectedRowKey);
    onReplace?.(selectedRowKey, row?.version ?? null);
  };

  // 1200px clears the Quality Benchmarks table min-width (expand + metrics +
  // download) plus modal body padding, so the dialog does not scroll sideways.
  return (
    <SparkModalPortal open={open} onClose={onCancel} size="wide" width={1200}>
      <Modal
        title="Replace Benchmark"
        subtitle="Select which benchmark run to use for this version's evaluation results"
        showSubtitle
        showClose
        onCloseClick={onCancel}
        showOverlay={false}
        fixedHeight={false}
        showFooter
        tertiaryLabel="Cancel"
        onTertiaryClick={onCancel}
        primaryLabel="Replace"
        primaryDisabled={replaceDisabled}
        onPrimaryClick={handleReplace}
      >
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 180,
            }}
          >
            <Spinner size="large" />
          </div>
        ) : (
          <ModelDetailsQualityBenchmarksTable
            entries={entries}
            taskType={taskType}
            teacherName={teacherName}
            selectedRowKey={selectedRowKey}
            onSelectRow={(record) => setSelectedRowKey(record?.distillation_event_id ?? null)}
            onDownload={handleDownload}
          />
        )}
      </Modal>
    </SparkModalPortal>
  );
};

export default ReplaceBenchmarkModal;
